import { api } from './client';
import { endpoints } from './endpoints';

// -- profile -----------------------------------------------------------------
export function getProfile() {
  return api.get(endpoints.students.me);
}

export function updateProfile(input) {
  return api.patch(endpoints.students.me, input);
}

export function getCompletion() {
  return api.get(endpoints.students.completion);
}

export function getDashboard() {
  return api.get(endpoints.students.dashboard);
}

// -- profile picture ---------------------------------------------------------
// Both return the whole profile, so the caller refreshes one query rather
// than reconciling a URL it was handed separately.
export function uploadPhoto(file) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(endpoints.students.photo, form);
}

export function removePhoto() {
  return api.delete(endpoints.students.photo);
}

export function uploadVideo(file) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(endpoints.students.video, form);
}

export function removeVideo() {
  return api.delete(endpoints.students.video);
}

// -- career guidance ---------------------------------------------------------
/**
 * Read a free-text goal and get back one proposed catalogue category.
 *
 * A proposal, not a save: the backend deliberately writes nothing, so the
 * student confirms the reading before it becomes their target field.
 */
export function suggestCareerGoal(goal) {
  return api.post(endpoints.students.careerGoalSuggest, { goal });
}

/**
 * What open listings in a field are currently asking for. Omit the id to use
 * the student's own saved target; pass one to preview a field first.
 */
export function getCareerPath(jobCategoryId) {
  return api.get(endpoints.students.careerPath, {
    params: jobCategoryId ? { job_category_id: jobCategoryId } : {},
  });
}

/**
 * A step-by-step roadmap from a goal to an internship in a field.
 *
 * Both fields are optional -- the saved goal and field are used when absent.
 * `refresh` asks for a new take instead of the cached plan. The first request
 * for a goal writes the plan, which can take a while, so this one call gets a
 * longer timeout than the client's default.
 */
export function getCareerRoadmap({ goal, jobCategoryId, refresh = false } = {}) {
  return api.post(
    endpoints.students.careerRoadmap,
    {
      ...(goal ? { goal } : {}),
      ...(jobCategoryId ? { job_category_id: jobCategoryId } : {}),
      refresh,
    },
    { timeout: 90_000 },
  );
}

/**
 * Check one of the student's CVs against a goal and field: the counted fit
 * (which in-demand skills the CV shows), the CV review's counted quality, and
 * suggestions written by AI or from the gap. Writes nothing to the profile.
 * Goal and field fall back to the saved ones when absent. A first check may
 * run a generation, hence the long timeout.
 */
export function checkCvAgainstGoal({ resumeId, goal, jobCategoryId, refresh = false }) {
  return api.post(
    endpoints.students.careerCvCheck,
    {
      resume_id: resumeId,
      ...(goal ? { goal } : {}),
      ...(jobCategoryId ? { job_category_id: jobCategoryId } : {}),
      refresh,
    },
    { timeout: 90_000 },
  );
}

// -- university email --------------------------------------------------------
export function requestUniversityEmailCode(input) {
  return api.post(endpoints.students.universityEmail, input);
}

export function verifyUniversityEmail(code) {
  return api.post(endpoints.students.verifyUniversityEmail, { code });
}

// -- education ---------------------------------------------------------------
/** The student's own profile in the shareable page's shape. */
export function getPortfolioView() {
  return api.get('/students/me/portfolio-view');
}

export function listEducation() {
  return api.get(endpoints.students.education);
}

export function addEducation(input) {
  return api.post(endpoints.students.education, input);
}

export function updateEducation(id, input) {
  return api.put(endpoints.students.educationById(id), input);
}

export function deleteEducation(id) {
  return api.delete(endpoints.students.educationById(id));
}

// Uploading the diploma is the request for verification; there is no separate
// submit. Both calls return the updated education entry.
export function uploadDiploma(educationId, file) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(endpoints.students.educationDiploma(educationId), form);
}

export function getDiplomaDownloadUrl(educationId, { inline = false } = {}) {
  return api.get(endpoints.students.educationDiplomaDownload(educationId), {
    params: inline ? { inline: true } : undefined,
  });
}

export function deleteDiploma(educationId) {
  return api.delete(endpoints.students.educationDiploma(educationId));
}

// -- experience --------------------------------------------------------------
export function listExperience() {
  return api.get(endpoints.students.experience);
}

export function addExperience(input) {
  return api.post(endpoints.students.experience, input);
}

export function updateExperience(id, input) {
  return api.put(endpoints.students.experienceById(id), input);
}

export function deleteExperience(id) {
  return api.delete(endpoints.students.experienceById(id));
}

// -- skills ------------------------------------------------------------------
export function listSkills() {
  return api.get(endpoints.students.skills);
}

export function addSkill(input) {
  return api.post(endpoints.students.skills, input);
}

export function removeSkill(skillId) {
  return api.delete(endpoints.students.skillById(skillId));
}

// -- certifications ----------------------------------------------------------
export function listCertifications() {
  return api.get(endpoints.students.certifications);
}

export function addCertification(input) {
  return api.post(endpoints.students.certifications, input);
}

export function updateCertification(id, input) {
  return api.put(endpoints.students.certificationById(id), input);
}

export function deleteCertification(id) {
  return api.delete(endpoints.students.certificationById(id));
}

// The certificate file, checked by an admin. Uploading is the request for
// verification, as with a diploma. Upload and delete return the certification.
export function uploadCertificationFile(certificationId, file) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(endpoints.students.certificationFile(certificationId), form);
}

export function getCertificationFileDownloadUrl(certificationId, { inline = false } = {}) {
  return api.get(endpoints.students.certificationFileDownload(certificationId), {
    params: inline ? { inline: true } : undefined,
  });
}

export function deleteCertificationFile(certificationId) {
  return api.delete(endpoints.students.certificationFile(certificationId));
}

// -- portfolio ---------------------------------------------------------------
export function listPortfolio() {
  return api.get(endpoints.students.portfolio);
}

export function addPortfolio(input) {
  return api.post(endpoints.students.portfolio, input);
}

export function updatePortfolio(id, input) {
  return api.put(endpoints.students.portfolioById(id), input);
}

export function deletePortfolio(id) {
  return api.delete(endpoints.students.portfolioById(id));
}

// -- CV ----------------------------------------------------------------------
export function listResumes() {
  return api.get(endpoints.students.resumes);
}

/**
 * Upload a CV. The server makes it the primary unless `makePrimary` is false,
 * which is for a one-off CV that should not replace the one on file.
 */
export function uploadResume(file, { makePrimary = true } = {}) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(endpoints.students.resumes, form, {
    params: makePrimary ? undefined : { make_primary: false },
  });
}

export function getResumeDownloadUrl(id) {
  return api.get(endpoints.students.resumeDownload(id));
}

export function setPrimaryResume(id) {
  return api.post(endpoints.students.resumePrimary(id));
}

export function deleteResume(id) {
  return api.delete(endpoints.students.resumeById(id));
}

// -- Reading a CV ------------------------------------------------------------
// Reading only ever returns suggestions. `applyResumeParse` is the one call
// that changes the profile, and only with what was ticked.
export function parseResume(id) {
  return api.post(endpoints.students.resumeParse(id));
}

export function getResumeParse(id) {
  return api.get(endpoints.students.resumeParse(id));
}

export function applyResumeParse(id, selection) {
  return api.post(endpoints.students.resumeParseApply(id), selection);
}

/**
 * Score a CV and get improvement suggestions. The score is counted from the
 * CV; the feedback is written by AI when available (`source: 'ai'`). A fresh
 * review can take 10-20 seconds, hence the long timeout. `refresh` asks for
 * new feedback instead of the cached one.
 */
export function reviewResume(id, { refresh = false } = {}) {
  return api.post(endpoints.students.resumeReview(id), null, {
    params: refresh ? { refresh: true } : {},
    timeout: 90_000,
  });
}

/**
 * A CV's counted score, grade and checklist alone -- no AI, no allowance, so
 * it is instant while `reviewResume` writes the feedback.
 */
export function getResumeScore(id) {
  return api.get(endpoints.students.resumeScore(id));
}

/**
 * Where one CV falls short: skills against the goal, timeline gaps, missing
 * or weak sections, and CV-against-profile differences. Counted by rules on
 * the server -- free, no AI, and the same CV always gives the same answer.
 */
export function getResumeGapAnalysis(id) {
  return api.get(endpoints.students.resumeGapAnalysis(id));
}

// -- shareable portfolio page ------------------------------------------------
// `/students/me/portfolio` is the project list; the sharing settings are
// separate. Each call returns `{ enabled, slug, path }`.
export function getPortfolioSharing() {
  return api.get(endpoints.students.publicPortfolio);
}

export function setPortfolioSharing(enabled) {
  return api.put(endpoints.students.publicPortfolio, { enabled });
}

export function regeneratePortfolioLink() {
  return api.post(endpoints.students.publicPortfolioRegenerate);
}
